import React from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CalendarCheck2,
  CalendarClock,
  CheckCircle2,
  Clock,
  Inbox as InboxIcon,
  MessageCircle,
  RefreshCcw,
  UserCheck,
  UserPlus,
} from "@/components/icons";
import { useStore } from "@/store/store";
import {
  AttendanceBadge,
  BookingStateBadge,
  ChannelBadge,
  IliaAvatar,
  OwnershipBadge,
  StageBadge,
  StatusChip,
} from "@/components/askilia/atoms";
import { Button } from "@/components/ui/button";
import { money, fmtTime, sameDay, initials } from "@/lib/format";
import { getPotentialSale } from "@/lib/commercial";
import { bookingResourceIds } from "@/lib/scheduling";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n";

const PANEL = "overflow-hidden rounded-xl border border-border bg-surface shadow-xs";
const TODAY_VIEW_KEY = "askilia.today.view";

function Avatar({ name, size = "md", className }) {
  const { localize } = useI18n();
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full bg-secondary font-semibold text-secondary-foreground",
        size === "sm" ? "h-8 w-8 text-[11px]" : "h-10 w-10 text-sm",
        className
      )}
    >
      {initials(localize(name))}
    </span>
  );
}

function PanelHeader({ title, description, action, className }) {
  return (
    <div className={cn("flex min-h-[58px] flex-col items-start justify-between gap-3 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:gap-4", className)}>
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {description ? <p className="mt-0.5 text-xs text-muted-foreground">{description}</p> : null}
      </div>
      {action ? <div className="w-full shrink-0 sm:w-auto">{action}</div> : null}
    </div>
  );
}

function Segmented({ value, onChange, options, ariaLabel, controls }) {
  return (
    <div className="inline-flex shrink-0 rounded-lg border border-border bg-muted/35 p-0.5" role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          aria-controls={controls}
          className={cn(
            "min-h-9 rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            value === option.value
              ? "bg-surface text-foreground shadow-xs"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function relativeTime(value, t, formatNumber) {
  const elapsed = Math.max(0, Date.now() - new Date(value).getTime());
  const minutes = Math.floor(elapsed / 60000);
  if (minutes < 1) return t("operations.time.justNow");
  if (minutes < 60) return t("operations.time.minutesAgo", { count: formatNumber(minutes) });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("operations.time.hoursAgo", { count: formatNumber(hours) });
  return t("operations.time.daysAgo", { count: formatNumber(Math.floor(hours / 24)) });
}

function greetingFor(date, t) {
  const hour = date.getHours();
  if (hour < 12) return t("today.greeting.morning");
  if (hour < 18) return t("today.greeting.afternoon");
  return t("today.greeting.evening");
}

function decisionLabel(conversation, t) {
  const request = `${conversation?.handoff?.request || ""}`.toLowerCase();
  if (conversation?.journey === "reschedule") return t("today.decision.findNewTime");
  if (request.includes("instalment") || request.includes("payment")) return t("today.decision.decideTerms");
  if (conversation?.draft) return t("today.decision.reviewDraft");
  return t("today.decision.reviewHandoff");
}

function readinessLabel(conversation, t) {
  if (conversation?.journey === "reschedule") return t("today.decision.optionsReady");
  if (conversation?.draft) return t("today.decision.draftReady");
  return t("today.decision.briefReady");
}

export default function Today() {
  const { state } = useStore();
  const { t, localize, formatDate, formatNumber } = useI18n();
  const nav = useNavigate();
  const [decisionView, setDecisionView] = React.useState("priority");
  const [pageView, setPageView] = React.useState(() => {
    if (typeof window === "undefined") return "overview";
    const stored = window.localStorage.getItem(TODAY_VIEW_KEY);
    return ["overview", "workflow"].includes(stored) ? stored : "overview";
  });
  const now = new Date();

  React.useEffect(() => {
    window.localStorage.setItem(TODAY_VIEW_KEY, pageView);
  }, [pageView]);

  const custById = React.useCallback(
    (id) => state.customers.find((customer) => customer.id === id),
    [state.customers]
  );
  const svcById = React.useCallback(
    (id) => state.services.find((service) => service.id === id),
    [state.services]
  );

  const openConversations = state.conversations.filter(
    (conversation) => !conversation.archivedAt && conversation.lifecycle !== "resolved"
  );
  const needsYou = openConversations.filter((conversation) => conversation.waitingOn === "team");
  const newEnquiries = openConversations.filter(
    (conversation) => conversation.lifecycle === "ilia" && conversation.journey === "lead"
  );
  const waitingCustomers = openConversations.filter((conversation) => conversation.waitingOn === "customer");
  const closedChats = state.conversations.filter(
    (conversation) => !conversation.archivedAt && conversation.lifecycle === "resolved"
  );
  const orphanOpps = state.customers.filter(
    (customer) => !customer.nextAction && ["Qualifying", "Qualified", "Offered"].includes(customer.stage)
  );
  const todaysBookings = state.bookings
    .filter((booking) => sameDay(booking.start, now) && booking.state !== "cancelled")
    .sort((left, right) => new Date(left.start) - new Date(right.start));
  const overviewBookings = todaysBookings.slice(0, 6);
  const noShowRecovery = state.bookings.filter((booking) => booking.state === "noshow");
  const pendingBookings = todaysBookings.filter((booking) => ["pending", "hold"].includes(booking.state));
  const waitingOver24h = waitingCustomers.filter(
    (conversation) => now.getTime() - new Date(conversation.lastActivity).getTime() > 24 * 60 * 60 * 1000
  );

  const decisionRows = [...needsYou].sort((left, right) => {
    const leftValue = getPotentialSale(custById(left.customerId)).amount;
    const rightValue = getPotentialSale(custById(right.customerId)).amount;
    if (decisionView === "value") return rightValue - leftValue;
    if (decisionView === "recent") return new Date(right.lastActivity) - new Date(left.lastActivity);
    const urgency = { high: 3, normal: 2, low: 1 };
    return (urgency[right.urgency] || 0) - (urgency[left.urgency] || 0)
      || rightValue - leftValue
      || new Date(left.lastActivity) - new Date(right.lastActivity);
  });

  const decisionValue = needsYou.reduce(
    (sum, conversation) => sum + getPotentialSale(custById(conversation.customerId)).amount,
    0
  );
  const oldestDecision = [...needsYou].sort(
    (left, right) => new Date(left.lastActivity) - new Date(right.lastActivity)
  )[0];

  const bookedValue = todaysBookings.reduce((sum, booking) => sum + Number(booking.price || 0), 0);
  const recoverableValue = noShowRecovery.reduce((sum, booking) => sum + Number(booking.price || 0), 0)
    + orphanOpps.reduce((sum, customer) => sum + getPotentialSale(customer).amount, 0);
  const expiredChannels = state.channels.filter((channel) => channel.status !== "connected");
  const pendingChanges = state.erp?.pendingChanges || [];
  const firstName = localize(state.user.name).split(" ")[0];
  const workflowModel = {
    state,
    nav,
    custById,
    svcById,
    needsYou,
    newEnquiries,
    orphanOpps,
    todaysBookings,
    noShowRecovery,
    expiredChannels,
    pendingChanges,
  };

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      <section className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            {t("today.greeting.withName", { greeting: greetingFor(now, t), name: firstName })}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {formatDate(now, { weekday: "long", day: "numeric", month: "long" })}
            <span aria-hidden="true"> · </span>
            {localize(state.clinic.workspace)}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{t("today.summary.decisions", { count: formatNumber(needsYou.length) })}</span>
            <span aria-hidden="true">·</span>
            <span>{t("today.summary.valueInvolved", { value: money(decisionValue) })}</span>
            {oldestDecision ? (
              <>
                <span aria-hidden="true">·</span>
                <span>{t("today.summary.oldestRequest")} <strong className="font-semibold text-warning-soft-foreground">{relativeTime(oldestDecision.lastActivity, t, formatNumber)}</strong></span>
              </>
            ) : null}
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <IliaAvatar size={24} active surface={false} />
            <span>{t("today.summary.iliaInControl")}</span>
          </div>
        </div>

        <div className="flex w-full flex-col gap-3 lg:w-auto lg:min-w-[430px]">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-medium text-muted-foreground">{t("today.view.label")}</span>
            <Segmented
              value={pageView}
              onChange={setPageView}
              ariaLabel={t("today.view.aria")}
              controls="today-view-content"
              options={[
                { value: "overview", label: t("today.view.overview") },
                { value: "workflow", label: t("today.view.workflow") },
              ]}
            />
          </div>
          <div className="grid w-full grid-cols-3 gap-px overflow-hidden rounded-xl border border-border bg-border shadow-xs">
            <SummaryButton label={t("today.summary.active")} compactLabel={t("today.summary.activeCompact")} value={openConversations.length} onClick={() => nav("/inbox?filter=all")} />
            <SummaryButton label={t("today.summary.needsAttention")} compactLabel={t("today.summary.attentionCompact")} value={needsYou.length} warning onClick={() => nav("/inbox?filter=operator")} />
            <SummaryButton label={t("today.summary.resolved")} compactLabel={t("today.summary.resolvedCompact")} value={closedChats.length} onClick={() => nav("/inbox?filter=resolved")} />
          </div>
        </div>
      </section>

      <div id="today-view-content" className="min-w-0 max-w-full space-y-5 overflow-x-hidden" aria-live="polite">
      {pageView === "overview" ? (
        <>
      <div className="grid min-w-0 items-start gap-5 xl:grid-cols-12">
        <section className={cn(PANEL, "min-w-0 xl:col-span-7")}>
          <PanelHeader
            title={t("today.decisions.title")}
            description={t("today.decisions.description")}
            action={
              <Segmented
                value={decisionView}
                onChange={setDecisionView}
                ariaLabel={t("today.decisions.sortAria")}
                options={[
                  { value: "priority", label: t("today.decisions.sort.priority") },
                  { value: "value", label: t("today.decisions.sort.value") },
                  { value: "recent", label: t("today.decisions.sort.recent") },
                ]}
              />
            }
          />
          {decisionRows.length ? (
            <div className="divide-y divide-border">
              {decisionRows.slice(0, 4).map((conversation, index) => {
                const customer = custById(conversation.customerId);
                const sale = getPotentialSale(customer);
                const task = customer?.nextAction?.label || conversation.handoff?.request
                  || conversation.messages.at(-1)?.text || t("today.decisions.reviewConversation");
                return (
                  <div key={conversation.id} className="grid gap-3 px-4 py-3.5 transition-colors hover:bg-muted/25 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-accent-soft-foreground">
                          {index + 1}
                        </span>
                        <Avatar name={customer?.name} />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-foreground">{localize(customer?.name)}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <ChannelBadge channel={conversation.channel} />
                            <span className="text-[11px] text-muted-foreground">{relativeTime(conversation.lastActivity, t, formatNumber)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 min-w-0 sm:pl-[76px]">
                        <p className="line-clamp-2 text-sm font-medium leading-5 text-foreground">{localize(task)}</p>
                        <p className="mt-1 text-xs leading-5 text-muted-foreground">{readinessLabel(conversation, t)}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 pl-9 sm:min-w-[154px] sm:flex-col sm:items-stretch sm:pl-0 sm:text-right">
                      <div>
                        <p className="text-xs text-muted-foreground">{t("today.value.potential")}</p>
                        <p className="text-sm font-semibold tabular-nums text-foreground">{sale.hasValue ? money(sale.amount) : t("today.value.notSet")}</p>
                      </div>
                      <Button className="justify-center whitespace-nowrap" onClick={() => nav(`/inbox/${conversation.id}`)}>
                        {decisionLabel(conversation, t)}
                      </Button>
                    </div>
                  </div>
                );
              })}
              {decisionRows.length > 4 ? (
                <button type="button" onClick={() => nav("/inbox?filter=operator")} className="flex w-full items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold text-primary transition-colors hover:bg-muted/30">
                  {t("today.decisions.viewAll", { count: formatNumber(decisionRows.length) })} <ArrowRight className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          ) : (
            <div className="flex min-h-56 flex-col items-center justify-center gap-2 p-6 text-center">
              <CheckCircle2 className="h-6 w-6 text-success" />
              <p className="text-sm font-semibold">{t("today.decisions.emptyTitle")}</p>
              <p className="text-xs text-muted-foreground">{t("today.decisions.emptyDescription")}</p>
            </div>
          )}
        </section>

        <div className="min-w-0 space-y-5 xl:col-span-5">
          <CompactEnquiries model={workflowModel} />
          <CompactPipeline
            bookings={todaysBookings.length}
            bookedValue={bookedValue}
            enquiries={newEnquiries.length}
            qualified={state.customers.filter((customer) => ["Qualified", "Offered"].includes(customer.stage)).length}
            waiting={waitingCustomers.length}
            pending={pendingBookings.length}
            merges={state.customers.filter((customer) => customer.mergePending).length}
            onOpen={() => nav("/customers")}
          />
        </div>
      </div>

      <div className="grid min-w-0 items-start gap-5 xl:grid-cols-12">
        <div className="min-w-0 space-y-5 xl:col-span-8">
          <section className={PANEL}>
          <PanelHeader
            title={t("today.runSheet.title")}
            description={t("today.runSheet.summary", { count: formatNumber(todaysBookings.length), value: money(bookedValue) })}
            action={<Button variant="ghost" size="sm" className="whitespace-nowrap" onClick={() => nav("/calendar")}>{t("today.actions.openCalendar")} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>}
          />
          {overviewBookings.length ? (
            <>
            <div className="divide-y divide-border md:hidden">
              {overviewBookings.map((booking) => {
                const customer = custById(booking.customerId);
                const service = svcById(booking.serviceId);
                return (
                  <button key={booking.id} type="button" onClick={() => nav("/calendar")} className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/25">
                    <span className="w-12 shrink-0 text-sm font-semibold tabular-nums text-foreground">{fmtTime(booking.start)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">{localize(customer?.name)}</span>
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">{localize(service?.name)}</span>
                      <span className="mt-2 flex flex-wrap items-center gap-1.5">
                        <BookingStateBadge state={booking.state} />
                        <AttendanceBadge booking={booking} compact />
                      </span>
                    </span>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">{money(booking.price)}</span>
                  </button>
                );
              })}
            </div>
            <div className="hidden overflow-x-auto md:block">
              <div className="min-w-[760px]">
              <div className="grid grid-cols-[70px_58px_minmax(170px,1.25fr)_minmax(150px,1fr)_160px_92px] gap-3 border-b border-border bg-muted/20 px-4 py-2 text-[11px] font-medium text-muted-foreground">
                <span>{t("today.runSheet.time")}</span><span>{t("today.runSheet.length")}</span><span>{t("today.runSheet.serviceCustomer")}</span><span>{t("today.runSheet.doctorRoom")}</span><span>{t("today.runSheet.status")}</span><span className="text-right">{t("today.runSheet.value")}</span>
              </div>
              <div className="divide-y divide-border">
                {overviewBookings.map((booking) => {
                  const customer = custById(booking.customerId);
                  const service = svcById(booking.serviceId);
                  const assigned = bookingResourceIds(booking, state.services)
                    .map((id) => state.resources.find((resource) => resource.id === id)?.short)
                    .filter(Boolean);
                  return (
                    <button key={booking.id} type="button" onClick={() => nav("/calendar")} className="grid w-full grid-cols-[70px_58px_minmax(170px,1.25fr)_minmax(150px,1fr)_160px_92px] items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/25">
                      <span className="text-sm font-semibold tabular-nums text-foreground">{fmtTime(booking.start)}</span>
                      <span className="text-xs text-muted-foreground">{t("today.bookings.minutes", { count: formatNumber(service?.duration || 0) })}</span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-foreground">{localize(service?.name)}</span>
                        <span className="block truncate text-xs text-muted-foreground">{localize(customer?.name)}</span>
                      </span>
                      <span className="truncate text-xs text-muted-foreground">{assigned.map(localize).join(" · ") || t("today.runSheet.assignmentPending")}</span>
                      <span className="flex flex-wrap items-center gap-1.5">
                        <BookingStateBadge state={booking.state} />
                        <AttendanceBadge booking={booking} compact />
                      </span>
                      <span className="text-right text-sm font-semibold tabular-nums text-foreground">{money(booking.price)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            </div>
            {todaysBookings.length > overviewBookings.length ? (
              <button type="button" onClick={() => nav("/calendar")} className="flex w-full items-center justify-center gap-2 border-t border-border px-4 py-2.5 text-xs font-semibold text-primary transition-colors hover:bg-muted/30">
                {t("today.runSheet.viewAll", { count: formatNumber(todaysBookings.length) })} <ArrowRight className="h-4 w-4" />
              </button>
            ) : null}
            </>
          ) : (
            <EmptyPanel icon={CalendarClock} title={t("today.runSheet.emptyTitle")} description={t("today.runSheet.emptyDescription")} />
          )}
          </section>

          <CompactReview model={workflowModel} />
        </div>

        <section className={cn(PANEL, "min-w-0 xl:col-span-4")}>
          <PanelHeader
            title={t("today.loops.title")}
            description={t("today.loops.description")}
            action={<span className="text-xs font-semibold tabular-nums text-foreground">{t("today.loops.recoverableValue", { value: money(recoverableValue) })}</span>}
          />
          <div className="divide-y divide-border">
            <ClosingLoop
              icon={CalendarClock}
              tone="destructive"
              count={pendingBookings.length}
              title={t("today.loops.confirmations")}
              value={pendingBookings.reduce((sum, booking) => sum + Number(booking.price || 0), 0)}
              valueLabel={t("today.loops.atRisk")}
              action={t("today.loops.reviewConfirm")}
              onClick={() => nav("/calendar")}
            />
            <ClosingLoop
              icon={UserCheck}
              tone="warning"
              count={orphanOpps.length}
              title={t("today.loops.opportunities")}
              value={orphanOpps.reduce((sum, customer) => sum + getPotentialSale(customer).amount, 0)}
              valueLabel={t("today.loops.potential")}
              action={t("today.loops.createActions")}
              onClick={() => nav("/customers")}
            />
            <ClosingLoop
              icon={RefreshCcw}
              tone="destructive"
              count={noShowRecovery.length}
              title={t("today.loops.recoveries")}
              value={noShowRecovery.reduce((sum, booking) => sum + Number(booking.price || 0), 0)}
              valueLabel={t("today.loops.recoverable")}
              action={t("today.loops.startRecoveries")}
              onClick={() => nav("/customers")}
            />
            <ClosingLoop
              icon={Clock}
              tone="primary"
              count={waitingOver24h.length}
              title={t("today.loops.waitingCustomers")}
              action={t("today.loops.checkRespond")}
              onClick={() => nav("/inbox?filter=all")}
            />
          </div>
        </section>
      </div>

        </>
      ) : (
        <WorkflowToday model={workflowModel} />
      )}
      </div>
    </div>
  );
}

function CompactEnquiries({ model, className }) {
  const { nav, custById, newEnquiries } = model;
  const { t, localize, formatNumber } = useI18n();
  const potential = newEnquiries.reduce(
    (sum, conversation) => sum + getPotentialSale(custById(conversation.customerId)).amount,
    0
  );

  return (
    <section className={cn(PANEL, className)}>
      <PanelHeader
        title={t("today.enquiries.title")}
        description={t("today.enquiries.description")}
        action={
          <div className="text-right">
            <p className="text-sm font-semibold tabular-nums text-foreground">{money(potential)}</p>
            <p className="text-[10px] text-muted-foreground">{t("today.value.potentialValue")}</p>
          </div>
        }
      />
      {newEnquiries.length ? (
        <div className="divide-y divide-border">
          {newEnquiries.slice(0, 4).map((conversation) => {
            const customer = custById(conversation.customerId);
            const sale = getPotentialSale(customer);
            return (
              <button
                key={conversation.id}
                type="button"
                onClick={() => nav(`/inbox/${conversation.id}`)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              >
                <IliaAvatar size={32} active surface={false} />
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <p className="truncate text-sm font-semibold text-foreground">{localize(customer?.name)}</p>
                    <ChannelBadge channel={conversation.channel} />
                  </div>
                  <p className="mt-1 truncate text-xs text-muted-foreground">
                    {localize(conversation.messages.at(-1)?.text) || t("today.enquiries.gathering")}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-semibold tabular-nums text-foreground">{sale.hasValue ? money(sale.amount) : t("today.value.notSet")}</p>
                  <p className="text-[10px] text-muted-foreground">{relativeTime(conversation.lastActivity, t, formatNumber)}</p>
                </div>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => nav("/inbox?filter=ilia")}
            className="flex w-full items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold text-primary transition-colors hover:bg-muted/30"
          >
            {t("today.enquiries.openQueue")} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <EmptyPanel icon={CheckCircle2} title={t("today.enquiries.emptyTitle")} description={t("today.enquiries.emptyDescription")} />
      )}
    </section>
  );
}

function CompactPipeline({ bookings, bookedValue, enquiries, qualified, waiting, pending, merges, onOpen }) {
  const { t, formatNumber } = useI18n();
  return (
    <section className={PANEL}>
      <PanelHeader
        title={t("today.pipeline.title")}
        description={t("today.pipeline.description")}
        action={<Button variant="ghost" size="sm" className="whitespace-nowrap" onClick={onOpen}>{t("today.pipeline.openOpportunities")} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>}
      />
      <div className="grid grid-cols-3 items-start gap-2 px-3 py-4 sm:px-4">
        <FunnelStep icon={UserPlus} value={formatNumber(enquiries)} label={t("today.pipeline.new")} />
        <FunnelStep icon={UserCheck} value={formatNumber(qualified)} label={t("today.pipeline.qualified")} />
        <FunnelStep icon={CalendarCheck2} value={formatNumber(bookings)} label={t("today.pipeline.bookedToday")} meta={money(bookedValue)} />
      </div>
      <div className="grid divide-y divide-border border-t border-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        <MiniOutcome value={formatNumber(waiting)} label={t("today.pipeline.awaitingCustomer")} />
        <MiniOutcome value={formatNumber(pending)} label={t("today.pipeline.pendingBookings")} tone="warning" />
        <MiniOutcome value={formatNumber(merges)} label={t("today.pipeline.identityReviews")} tone="warning" />
      </div>
    </section>
  );
}

function CompactReview({ model, compact = true }) {
  const { expiredChannels, nav, pendingChanges, state } = model;
  const { t, localize, formatNumber } = useI18n();
  const issueCount = expiredChannels.length + (pendingChanges.length ? 1 : 0);
  const visibleChannels = compact
    ? expiredChannels.slice(0, pendingChanges.length ? 2 : 3)
    : expiredChannels;
  const visibleCount = visibleChannels.length + (pendingChanges.length ? 1 : 0);

  return (
    <section className={PANEL}>
      <PanelHeader
        title={t("today.review.title")}
        description={t("today.review.description")}
        action={<span className="text-xs font-semibold tabular-nums text-foreground">{t("today.review.openCount", { count: formatNumber(issueCount) })}</span>}
      />
      <div className="divide-y divide-border p-2">
        {visibleChannels.map((channel) => (
          <ReviewIssue
            key={channel.id}
            icon={MessageCircle}
            title={t("today.review.channelDisconnected", { channel: localize(channel.name) })}
            description={t("today.review.channelDescription")}
            action={t("today.review.reconnect")}
            onClick={() => nav("/settings/channels")}
          />
        ))}
        {pendingChanges.length ? (
          <ReviewIssue
            icon={RefreshCcw}
            tone="warning"
            title={t("today.review.priceChanges", { count: formatNumber(pendingChanges.length), system: localize(state.erp?.name) || t("today.review.managementSystem") })}
            description={t("today.review.priceDescription")}
            action={t("today.review.reviewChanges")}
            onClick={() => nav("/settings/channels?review=imported")}
          />
        ) : null}
        {!issueCount ? (
          <EmptyPanel icon={CheckCircle2} title={t("today.review.emptyTitle")} description={t("today.review.emptyDescription")} />
        ) : null}
        {compact && issueCount > visibleCount ? (
          <button type="button" onClick={() => nav("/settings/channels")} className="flex w-full items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold text-primary transition-colors hover:bg-muted/30">
            {t("today.review.viewAll", { count: formatNumber(issueCount) })} <ArrowRight className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </section>
  );
}

function WorkflowToday({ model }) {
  const { t, formatNumber } = useI18n();
  const {
    needsYou,
    newEnquiries,
    noShowRecovery,
    orphanOpps,
  } = model;

  return (
    <div className="min-w-0 max-w-full space-y-5 overflow-x-hidden">
      <p className="sr-only">
        {t("today.workflow.summary", {
          decisions: formatNumber(needsYou.length),
          enquiries: formatNumber(newEnquiries.length),
          opportunities: formatNumber(orphanOpps.length),
          recoveries: formatNumber(noShowRecovery.length),
        })}
      </p>

      <WorkflowAttention model={model} />
      <WorkflowEnquiries model={model} />
      <WorkflowBookings model={model} />
      <WorkflowOpportunities model={model} />
      <WorkflowRecovery model={model} />

      <CompactReview model={model} compact={false} />
    </div>
  );
}

function WorkflowAttention({ model }) {
  const { nav, custById, needsYou } = model;
  const { t, localize, formatNumber } = useI18n();
  return (
    <section className={cn(PANEL, "min-w-0 max-w-full")}>
      <PanelHeader
        title={t("today.attention.title")}
        description={t("today.attention.description")}
        action={<span className="text-xs font-semibold tabular-nums text-foreground">{t("today.attention.count", { count: formatNumber(needsYou.length) })}</span>}
      />
      {needsYou.length ? (
        <div className="divide-y divide-border">
          {needsYou.map((conversation) => {
            const customer = custById(conversation.customerId);
            const sale = getPotentialSale(customer);
            return (
              <div key={conversation.id} className="flex min-w-0 flex-col gap-3 px-4 py-4 lg:flex-row lg:items-center">
                <Avatar name={customer?.name} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-foreground">{localize(customer?.name)}</p>
                    <ChannelBadge channel={conversation.channel} />
                    <StatusChip tone="warning">{t("today.attention.operatorRequired")}</StatusChip>
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground sm:line-clamp-1">
                    {localize(conversation.handoff?.request || conversation.messages.at(-1)?.text) || t("today.attention.reviewHandoff")}
                  </p>
                </div>
                <PotentialValue customer={customer} activity={conversation.lastActivity} />
                <Button className="w-full justify-center lg:w-auto lg:shrink-0" onClick={() => nav(`/inbox/${conversation.id}`)}>
                  {t("today.actions.openChat")} <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyPanel icon={CheckCircle2} title={t("today.attention.emptyTitle")} description={t("today.attention.emptyDescription")} />
      )}
    </section>
  );
}

function WorkflowEnquiries({ model }) {
  const { nav, custById, newEnquiries } = model;
  const { t, localize } = useI18n();
  return (
    <section className={cn(PANEL, "min-w-0 max-w-full")}>
      <PanelHeader
        title={t("today.enquiries.title")}
        description={t("today.enquiries.workflowDescription")}
        action={<Button variant="ghost" size="sm" className="whitespace-nowrap" onClick={() => nav("/inbox?filter=ilia")}>{t("today.actions.openQueue")} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>}
      />
      {newEnquiries.length ? (
        <div className="divide-y divide-border">
          {newEnquiries.map((conversation) => {
            const customer = custById(conversation.customerId);
            return (
              <div key={conversation.id} className="flex min-w-0 flex-col gap-3 px-4 py-4 lg:flex-row lg:items-center">
                <IliaAvatar size={40} active />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-foreground">{localize(customer?.name)}</p>
                    <ChannelBadge channel={conversation.channel} />
                    <OwnershipBadge owner="Ilia" />
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground sm:line-clamp-1">{localize(conversation.messages.at(-1)?.text)}</p>
                </div>
                <PotentialValue customer={customer} activity={conversation.lastActivity} />
                <Button variant="outline" className="w-full justify-center lg:w-auto lg:shrink-0" onClick={() => nav(`/inbox/${conversation.id}`)}>
                  {t("today.actions.watchChat")} <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyPanel icon={CheckCircle2} title={t("today.enquiries.workflowEmptyTitle")} description={t("today.enquiries.workflowEmptyDescription")} />
      )}
    </section>
  );
}

function WorkflowBookings({ model }) {
  const { nav, custById, svcById, state, todaysBookings } = model;
  const { t, localize, formatNumber } = useI18n();
  const total = todaysBookings.reduce((sum, booking) => sum + Number(booking.price || 0), 0);
  return (
    <section className={cn(PANEL, "min-w-0 max-w-full")}>
      <PanelHeader
        title={t("today.bookings.title")}
        description={t("today.bookings.description")}
        action={<Button variant="ghost" size="sm" className="whitespace-nowrap" onClick={() => nav("/calendar")}>{t("today.actions.openCalendar")} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>}
      />
      {todaysBookings.length ? (
        <div className="divide-y divide-border">
          {todaysBookings.map((booking) => {
            const customer = custById(booking.customerId);
            const service = svcById(booking.serviceId);
            const assigned = bookingResourceIds(booking, state.services)
              .map((id) => state.resources.find((resource) => resource.id === id)?.short)
              .filter(Boolean)
              .join(" + ");
            return (
              <button key={booking.id} type="button" onClick={() => nav("/calendar")} className="flex min-w-0 w-full flex-col gap-3 px-4 py-4 text-left transition-colors hover:bg-muted/25 lg:flex-row lg:items-center">
                <div className="w-16 shrink-0">
                  <p className="text-sm font-semibold tabular-nums text-foreground">{fmtTime(booking.start)}</p>
                  <p className="text-[11px] text-muted-foreground">{t("today.bookings.minutes", { count: formatNumber(service?.duration || 0) })}</p>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">{localize(customer?.name)}</p>
                  <p className="mt-1 truncate text-xs text-muted-foreground">{localize(service?.name)} · {assigned.split(" + ").filter(Boolean).map(localize).join(" + ") || t("today.runSheet.assignmentPending")}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                  <BookingStateBadge state={booking.state} />
                  <AttendanceBadge booking={booking} compact />
                </div>
                <p className="text-sm font-semibold tabular-nums text-foreground lg:w-20 lg:text-right">{money(booking.price)}</p>
              </button>
            );
          })}
          <div className="flex items-center justify-between bg-muted/15 px-4 py-3 text-xs text-muted-foreground">
            <span>{t("today.bookings.count", { count: formatNumber(todaysBookings.length) })}</span>
            <strong className="font-semibold tabular-nums text-foreground">{t("today.bookings.scheduled", { value: money(total) })}</strong>
          </div>
        </div>
      ) : (
        <EmptyPanel icon={CalendarClock} title={t("today.runSheet.emptyTitle")} description={t("today.runSheet.emptyDescription")} />
      )}
    </section>
  );
}

function WorkflowOpportunities({ model }) {
  const { nav, orphanOpps } = model;
  const { t, localize, formatNumber } = useI18n();
  return (
    <section className={cn(PANEL, "min-w-0 max-w-full")}>
      <PanelHeader
        title={t("today.opportunities.title")}
        description={t("today.opportunities.description")}
        action={<span className="text-xs font-semibold tabular-nums text-foreground">{t("today.opportunities.openCount", { count: formatNumber(orphanOpps.length) })}</span>}
      />
      {orphanOpps.length ? (
        <div className="divide-y divide-border">
          {orphanOpps.map((customer) => (
            <div key={customer.id} className="flex min-w-0 flex-col gap-3 px-4 py-4 lg:flex-row lg:items-center">
              <Avatar name={customer.name} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-foreground">{localize(customer.name)}</p>
                  <StageBadge stage={customer.stage} />
                </div>
                <p className="mt-1 truncate text-xs text-muted-foreground">{localize(customer.qualification?.[0]?.value) || t("today.opportunities.serviceUnknown")} · {t("today.opportunities.lastSeen", { time: relativeTime(customer.lastInteraction, t, formatNumber) })}</p>
              </div>
              <PotentialValue customer={customer} />
              <Button variant="outline" className="w-full justify-center whitespace-nowrap lg:w-auto lg:shrink-0" onClick={() => nav(`/customers/${customer.id}`)}>{t("today.opportunities.setNextAction")}</Button>
            </div>
          ))}
        </div>
      ) : (
        <EmptyPanel icon={CheckCircle2} title={t("today.opportunities.emptyTitle")} description={t("today.opportunities.emptyDescription")} />
      )}
    </section>
  );
}

function WorkflowRecovery({ model }) {
  const { nav, custById, svcById, noShowRecovery } = model;
  const { t, localize, formatNumber } = useI18n();
  const total = noShowRecovery.reduce((sum, booking) => sum + Number(booking.price || 0), 0);
  return (
    <section className={cn(PANEL, "min-w-0 max-w-full")}>
      <PanelHeader
        title={t("today.recovery.title")}
        description={t("today.recovery.description")}
        action={<span className="text-xs font-semibold tabular-nums text-destructive-soft-foreground">{t("today.recovery.recoverableValue", { value: money(total) })}</span>}
      />
      {noShowRecovery.length ? (
        <div className="divide-y divide-border">
          {noShowRecovery.map((booking) => {
            const customer = custById(booking.customerId);
            const service = svcById(booking.serviceId);
            return (
              <div key={booking.id} className="flex min-w-0 flex-col gap-3 px-4 py-4 lg:flex-row lg:items-center">
                <Avatar name={customer?.name} className="bg-destructive-soft text-destructive-soft-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-foreground">{localize(customer?.name)}</p>
                    <StatusChip tone="destructive" emphasis="critical">{t("today.recovery.missedService", { service: localize(service?.name) })}</StatusChip>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{t("today.recovery.noShowTime", { time: relativeTime(booking.start, t, formatNumber) })}</p>
                </div>
                <div className="text-left lg:text-right">
                  <p className="text-[10px] text-muted-foreground">{t("today.recovery.missedValue")}</p>
                  <p className="text-sm font-semibold tabular-nums text-destructive-soft-foreground">{money(booking.price)}</p>
                </div>
                <Button variant="outline" className="w-full justify-center lg:w-auto lg:shrink-0" onClick={() => nav(`/customers/${customer.id}`)}>
                  <RefreshCcw className="mr-1.5 h-4 w-4" /> {t("today.recovery.start")}
                </Button>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyPanel icon={CheckCircle2} title={t("today.recovery.emptyTitle")} description={t("today.recovery.emptyDescription")} />
      )}
    </section>
  );
}

function PotentialValue({ customer, activity }) {
  const sale = getPotentialSale(customer);
  const { t, formatNumber } = useI18n();
  return (
    <div className="min-w-0 shrink-0 text-left lg:text-right" title={sale.basis}>
      <p className="text-[10px] font-medium text-muted-foreground">{t("today.value.potential")}</p>
      <p className="text-sm font-semibold tabular-nums text-foreground">{sale.hasValue ? money(sale.amount) : t("today.value.notSet")}</p>
      {activity ? <p className="mt-0.5 text-[10px] text-muted-foreground">{relativeTime(activity, t, formatNumber)}</p> : null}
    </div>
  );
}

function EmptyPanel({ icon: Icon, title, description }) {
  return (
    <div className="flex min-h-28 flex-col items-center justify-center gap-1.5 px-4 py-6 text-center">
      <Icon className="h-5 w-5 text-success" />
      <p className="text-sm font-semibold text-foreground">{title}</p>
      {description ? <p className="max-w-sm text-xs text-muted-foreground">{description}</p> : null}
    </div>
  );
}

function SummaryButton({ label, compactLabel = label, value, onClick, warning = false }) {
  const { formatNumber } = useI18n();
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex min-w-0 items-center gap-2 bg-surface px-2 py-3 text-left transition-colors hover:bg-muted/35 focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:gap-3 sm:px-4",
        warning && "ring-1 ring-inset ring-warning/55 hover:bg-warning-soft/20"
      )}
    >
      <span className={cn("inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground", warning && "text-warning-soft-foreground")}>
        {warning ? <AlertTriangle className="h-4 w-4" /> : <InboxIcon className="h-4 w-4" />}
      </span>
      <span className="min-w-0">
        <span className="block text-xl font-semibold leading-none tabular-nums text-foreground">{formatNumber(value)}</span>
        <span className="mt-1 block text-[11px] font-medium leading-4 text-muted-foreground sm:hidden">{compactLabel}</span>
        <span className="mt-1 hidden text-[11px] font-medium leading-4 text-muted-foreground sm:block">{label}</span>
      </span>
    </button>
  );
}

function ClosingLoop({ icon: Icon, tone, count, title, value, valueLabel, action, onClick }) {
  const iconTone = {
    destructive: "bg-destructive-soft text-destructive-soft-foreground",
    warning: "bg-warning-soft text-warning-soft-foreground",
    primary: "bg-primary-soft text-primary-soft-foreground",
  };
  return (
    <div className="grid grid-cols-[36px_minmax(0,1fr)] gap-x-3 gap-y-2.5 px-4 py-3.5">
      <span className={cn("inline-flex h-9 w-9 items-center justify-center rounded-lg", iconTone[tone])}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-5 text-foreground">{count} {title}</p>
        {value ? <p className="mt-0.5 text-xs font-medium text-muted-foreground"><span className="tabular-nums text-foreground">{money(value)}</span> {valueLabel}</p> : null}
      </div>
      <Button variant="outline" size="sm" className="col-start-2 w-fit max-w-full whitespace-normal text-left leading-4" onClick={onClick}>{action}</Button>
    </div>
  );
}

function FunnelStep({ icon: Icon, value, label, meta }) {
  return (
    <div className="min-w-0 text-center">
      <span className="mx-auto inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-soft text-primary-soft-foreground"><Icon className="h-4 w-4" /></span>
      <p className="mt-2 text-xl font-semibold tabular-nums text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
      {meta ? <p className="mt-1 text-xs font-semibold tabular-nums text-primary-soft-foreground">{meta}</p> : null}
    </div>
  );
}

function MiniOutcome({ value, label, tone = "neutral" }) {
  const Icon = tone === "warning" ? AlertTriangle : Clock;
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <Icon className={cn("h-4 w-4 shrink-0", tone === "warning" ? "text-warning-soft-foreground" : "text-muted-foreground")} />
      <p className="text-xs text-muted-foreground"><strong className="mr-1 text-sm text-foreground">{value}</strong>{label}</p>
    </div>
  );
}

function ReviewIssue({ icon: Icon, tone = "destructive", title, description, action, onClick }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg px-3 py-3 sm:flex-row sm:items-center">
      <span className={cn(
        "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
        tone === "warning" ? "bg-warning-soft text-warning-soft-foreground" : "bg-destructive-soft text-destructive-soft-foreground"
      )}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
      <Button variant="outline" size="sm" className="w-fit whitespace-nowrap sm:shrink-0" onClick={onClick}>{action}</Button>
    </div>
  );
}
